<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import useVuelidate from '@vuelidate/core'
	import {
		email as emailRule,
		helpers,
		minLength,
		required,
		requiredIf,
	} from '@vuelidate/validators'
	import { formatDate } from '@vueuse/core'
	import { isEqual, omit } from 'lodash'
	import { computed } from 'vue'
	import { useRoute } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import RoundedBox from '@/components/RoundedBox.vue'
	import SkeletonLoader from '@/components/SkeletonLoader.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'
	import StyledSwitch from '@/components/StyledSwitch.vue'

	import { useAuth } from '@/composables/useAuth'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import useInvalidateQueryData from '@/composables/useInvalidateQueryData'

	import { GenderTypes } from '@/enums/genderTypes'

	import EditUserLogoVue from '@/modules/users/components/edit/EditUserLogo.vue'
	import useEditUserForm from '@/modules/users/composables/useEditUserForm'
	import useUser from '@/modules/users/composables/useUser'
	import { canEditProfile } from '@/modules/users/helpers/canEditProfile'
	import { canManageCredentialsOf } from '@/modules/users/helpers/roleHierarchy'
	import type { User } from '@/modules/users/interfaces/user'

	interface Props {
		user: User
	}

	const props = defineProps<Props>()

	const { form, isFetchingUser, isLoadingUser, userEdit, hasFormChanges, refetchUser } =
		useEditUserForm()

	const toast = useToast()

	const { user: userLogged } = useAuth()

	const { hasPermissionUser } = useHasPermissionUser()

	const { updateUserAsync } = useUser({ immediate: false })

	// Credenciales (correo de login + acceso al sistema) de un usuario de MAYOR
	// rango: el backend las canda en UpdateUserRequest (evita el account-takeover
	// swap-de-correo → forgot-password → entrar como él). Se refleja aquí para
	// que un Travel Manager no llene el campo y se coma un 422 al guardar: puede
	// editar todo el perfil del Master, menos estos dos.
	const canTouchCredentials = computed(() =>
		canManageCredentialsOf(userLogged.value, props.user)
	)

	const rules = {
		name: {
			required: helpers.withMessage('El nombre es requerido', required),
			minLength: helpers.withMessage(
				'El nombre debe tener al menos 3 caracteres',
				minLength(3)
			),
		},
		lastname: {
			required: helpers.withMessage('El apellido es requerido', required),
			minLength: helpers.withMessage(
				'El apellido debe tener al menos 3 caracteres',
				minLength(3)
			),
		},
		birthDate: {
			required: helpers.withMessage(
				'La fecha de nacimiento es requerida',
				required
			),
		},
		gender: {
			required: helpers.withMessage('El género es requerido', required),
		},
		// Dar acceso al sistema exige correo principal (el backend valida igual).
		email: {
			requiredIfAccess: helpers.withMessage(
				'Para dar acceso al sistema el usuario necesita un correo principal',
				requiredIf(() => form.haveAccess)
			),
			email: helpers.withMessage('Ingresa un correo válido', emailRule),
		},
	}

	const v$ = useVuelidate(rules, form)

	const adjustDateForAgeLimit = computed(() => {
		const date = new Date()
		date.setFullYear(date.getFullYear() - 18)
		return date
	})

	const onSubmit = async () => {
		v$.value.$touch()
		if (v$.value.$invalid) {
			for (const field of v$.value.$errors) {
				toast.error(field.$message as string)
			}
			return
		}

		try {
			const updatedForm = {
				...omit(form, ['supervisor', 'roles']),
				avatar: '',
				birthDate:
					form.birthDate ?
						(() => {
							// Convertir formato DD-MM-YYYY a YYYY-MM-DD
							const [day, month, year] = form.birthDate.split('-')
							return `${year}-${month}-${day}`
						})()
					:	undefined,
				// Correo vacío no se manda (es único y no se borra desde aquí).
				email: form.email || undefined,
			}

			const data = await updateUserAsync(updatedForm)

			if (data) {
				await refetchUser()
				// updateUser (useUser.ts) ya muestra su propio toast de éxito.
			}
		} catch (error: any) {
			console.log(error)

			// Muestra el error de campo del 422 en vez de tragárselo con un
			// mensaje genérico.
			const fieldErrors = error?.response?.data?.errors
			const firstFieldError =
				fieldErrors ?
					(Object.values(fieldErrors)[0] as string[] | undefined)?.[0]
				:	undefined

			toast.error(
				firstFieldError ||
					error.response?.data?.message ||
					'Error al actualizar el usuario'
			)
		}
	}
</script>

<template>
	<div class="space-y-6 no-scroll-on-focus">
		<!-- Profile Header Section - Full Width -->
		<RoundedBox class="bg-white">
			<div class="text-center mb-6">
				<h2 class="text-2xl font-semibold text-gray-800 mb-2">
					Información Personal
				</h2>
				<p class="text-gray-600 text-sm">
					Actualiza la información básica del perfil
				</p>
			</div>

			<!-- Full Width Logo Component -->
			<div class="w-full">
				<EditUserLogoVue
					:user="props.user"
					@refetch="refetchUser"
				/>
			</div>
		</RoundedBox>

		<form
			@submit.prevent="onSubmit"
			class="space-y-6"
		>
			<!-- Personal Information Section -->
			<RoundedBox class="bg-white">
				<div class="mb-6">
					<h3 class="text-lg font-medium text-gray-800 mb-1">
						Datos Personales
					</h3>
					<p class="text-sm text-gray-600">Información básica del usuario</p>
				</div>

				<div v-if="isLoadingUser || isFetchingUser">
					<SkeletonLoader
						variant="form-input"
						:count="4"
					/>
				</div>

				<div
					v-else
					class="grid grid-cols-1 md:grid-cols-2 gap-6"
				>
					<div class="space-y-4">
						<StyledInput
							is-required
							label="Nombre"
							v-model="form.name"
							:value="userEdit?.name"
							placeholder="Ingresa el nombre"
							class="transition-all duration-200 focus-within:scale-[1.01]"
							:error="v$.name.$errors.at(0)?.$message"
						/>
						<StyledInput
							label="Segundo nombre"
							v-model="form.secondName"
							:value="userEdit?.secondName"
							placeholder="Segundo nombre (opcional)"
							class="transition-all duration-200 focus-within:scale-[1.01]"
						/>
					</div>

					<div class="space-y-4">
						<StyledInput
							is-required
							label="Apellido"
							v-model="form.lastname"
							:value="userEdit?.lastname"
							placeholder="Ingresa el apellido"
							class="transition-all duration-200 focus-within:scale-[1.01]"
							:error="v$.lastname.$errors.at(0)?.$message"
						/>
						<StyledInput
							label="Segundo apellido"
							v-model="form.secondLastname"
							:value="userEdit?.secondLastname"
							placeholder="Segundo apellido (opcional)"
							class="transition-all duration-200 focus-within:scale-[1.01]"
						/>
					</div>
				</div>

				<div
					v-if="!isLoadingUser && !isFetchingUser"
					class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6"
				>
					<StyledDateInput
						label="Fecha de nacimiento"
						v-model="form.birthDate"
						:is-required="true"
						placeholder="Selecciona tu fecha de nacimiento"
						class="transition-all duration-200 focus-within:scale-[1.01]"
						:max-date="adjustDateForAgeLimit"
						:start-date="adjustDateForAgeLimit"
						:error="v$.birthDate.$errors.at(0)?.$message as string"
						:format="'dd-MM-yyyy'"
						:preview-format="'dd-MM-yyyy'"
						:value="
							formatDate(new Date(userEdit?.birthDate || ''), 'dd-MM-yyyy')
						"
					/>

					<StyledSelect
						:is-required="true"
						:options="
							Object.values(GenderTypes).map((gender) => ({
								label: gender,
								value: gender,
							}))
						"
						v-model="form.gender"
						label="Género"
						placeholder="Selecciona tu género"
						class="transition-all duration-200 focus-within:scale-[1.01]"
						:error="v$.gender.$errors.at(0)?.$message as string"
						:default-value="userEdit?.gender"
					/>
				</div>
			</RoundedBox>

			<!-- Acceso al sistema -->
			<RoundedBox class="bg-white">
				<div class="mb-6">
					<h3 class="text-lg font-medium text-gray-800 mb-1">
						Acceso al sistema
					</h3>
					<p class="text-sm text-gray-600">
						Controla si el usuario puede iniciar sesión en Enlacebooking
					</p>
				</div>

				<div class="flex items-center justify-between gap-4">
					<div>
						<p class="text-sm font-medium text-gray-800">
							{{
								form.haveAccess ? 'Con acceso al sistema' : 'Sin acceso al sistema'
							}}
						</p>
						<!-- Quitar el acceso NO da de baja al usuario: sigue siendo viajero y
						     sus supervisores o Travel Managers le siguen reservando. Sin
						     decirlo explícito, el cliente lo leía como "desactivar persona". -->
						<p class="text-xs text-gray-500 mt-0.5">
							{{
								form.haveAccess ?
									'El usuario puede iniciar sesión con su correo principal.'
								:	'El usuario seguirá activo y podrá tener reservaciones realizadas por sus supervisores o Travel Managers, pero no puede iniciar sesión.'
							}}
						</p>
					</div>
					<StyledSwitch
						:enabled="form.haveAccess"
						:disabled="!canTouchCredentials"
						@update:enabled="(value: boolean) => (form.haveAccess = value)"
					/>
				</div>

				<p
					v-if="!canTouchCredentials"
					class="text-xs text-amber-600 mt-3"
				>
					No puedes cambiar el correo de acceso ni el acceso al sistema de un
					usuario de mayor rango. El resto de su perfil sí lo puedes editar.
				</p>

				<!-- Siempre visible: sin acceso el correo es un dato de contacto opcional;
				     con acceso es el de inicio de sesión y se vuelve obligatorio. -->
				<div class="mt-4">
					<StyledInput
						:is-required="form.haveAccess"
						label="Correo principal"
						v-model="form.email"
						:value="userEdit?.email"
						:disabled="!canTouchCredentials"
						placeholder="correo@empresa.com"
						class="transition-all duration-200 focus-within:scale-[1.01]"
						:error="v$.email.$errors.at(0)?.$message as string"
					/>
					<p
						v-if="form.haveAccess && !userEdit?.email && canTouchCredentials"
						class="text-xs text-amber-600 mt-2"
					>
						Este usuario no tiene correo principal: captúralo aquí para poder
						darle acceso.
					</p>
				</div>
			</RoundedBox>

			<!-- Action Section -->

			<template v-if="isLoadingUser || isFetchingUser">
				<SkeletonLoader
					variant="card"
					:count="1"
				/>
			</template>

			<template v-else>
				<RoundedBox class="bg-white">
					<div class="flex justify-end gap-3">
						<StyledButton
							v-if="
								canEditProfile(userLogged?.id ?? 0, props.user.id) ||
								hasPermissionUser('update-users', 'App\\Models\\Company')
							"
							:label="hasFormChanges ? 'Guardar Cambios' : 'No hay cambios'"
							class="bg-gradient-to-r text-white transition-all duration-200 transform hover:scale-105 shadow-lg"
							:class="
								hasFormChanges ?
									'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
								:	'from-slate-400 to-gray-600 cursor-not-allowed opacity-50 hover:from-slate-600 hover:to-gray-600'
							"
							type="submit"
							:disabled="!hasFormChanges"
						/>
					</div>
				</RoundedBox>
			</template>
		</form>
	</div>
</template>
